import type { KeyValueRepository } from '@/repositories/key-value'
import type { StudentAccount, StudentWorkspace } from '@/entities/student-workspace'
import {
  assertMutationId,
  cloneWorkspaceDirectory,
  emptyWorkspaceDirectory,
  type WorkspaceCommitResult,
  type WorkspaceDirectory,
  type WorkspaceListener,
  type WorkspaceMutation,
  type WorkspaceRepository,
} from './WorkspaceRepository'

function readDirectory(raw: string | null): WorkspaceDirectory {
  if (!raw) return emptyWorkspaceDirectory()
  try {
    const parsed = JSON.parse(raw) as { state?: Partial<WorkspaceDirectory> } | Partial<WorkspaceDirectory>
    const state = ('state' in parsed && parsed.state ? parsed.state : parsed) as Partial<WorkspaceDirectory>
    if (!Array.isArray(state.studentIndex) || !state.workspaces || typeof state.workspaces !== 'object') {
      throw new Error('Legacy workspace snapshot is missing student directory data')
    }
    return {
      revision: 0,
      studentIndex: state.studentIndex as StudentAccount[],
      workspaces: state.workspaces as Record<string, StudentWorkspace>,
      serializedState: raw,
    }
  } catch (error) {
    if (error instanceof Error && error.message.startsWith('Legacy workspace')) throw error
    throw new Error('Legacy workspace snapshot is invalid')
  }
}

export class LegacyLocalStorageAdapter implements WorkspaceRepository {
  constructor(
    private readonly storage: KeyValueRepository,
    private readonly key = 'openelp-store-v3',
  ) {}

  async load(): Promise<WorkspaceDirectory> {
    return cloneWorkspaceDirectory(readDirectory(this.storage.get(this.key)))
  }

  async transact(_expectedRevision: number, mutationId: string, _mutation: WorkspaceMutation): Promise<WorkspaceCommitResult> {
    assertMutationId(mutationId)
    const snapshot = await this.load()
    return { status: 'readonly', mutationId, revision: snapshot.revision, snapshot }
  }

  subscribe(_listener: WorkspaceListener): () => void {
    return () => undefined
  }

  async exportRecovery(): Promise<WorkspaceDirectory> {
    return this.load()
  }
}
